// Knowledge › Builder: a job's progress (A2 step B). Ported from chatbot_front hub/jobSteps.js, jobLabels.js,
// usageFormat.js and jobStream.js with English labels, plus the SSE frame parser for the fetch-stream reader
// (EventSource cannot send the hub's bearer token). No imports on purpose: `node --test` loads this file directly.

// ── steps ───────────────────────────────────────────────────────────────────

/** Every step llm-tester emits; an unknown one renders as itself (that is how new steps get noticed). */
const STEP_LABEL: Record<string, string> = {
  discover: 'Discover pages',
  select_pages: 'Choose the pages that matter',
  collect_sources: 'Collect pages',
  build_snapshot: 'Build snapshot',
  load_snapshot: 'Load snapshot',
  chunk_corpus: 'Chunk (LLM)',
  persist_to_hub: 'Save to the bot’s documents',
  record_index_signature: 'Record index signature',
  skip_index: 'Skip index (nothing changed)',
  inline_collect: 'Read the given documents',
  generate_chunks: 'Generate chunks',
  persist_chunks: 'Save chunks',
  load_chunks: 'Load chunks',
  generate_questions: 'Generate questions (LLM)',
  persist_questions: 'Save questions',
  copy_corpus: 'Copy corpus',
};

export function stepLabel(step: string): string {
  return STEP_LABEL[step] || step;
}

const isObj = (v: unknown): v is Record<string, any> => !!v && Object.prototype.toString.call(v) === '[object Object]';

/** One line under a finished step, or null when there is nothing worth saying. */
export function stepDetail(step: string, data: unknown): string | null {
  if (!isObj(data)) return null;
  if (step === 'discover') {
    if (data.expanded === false) {
      // Coverage will be far smaller than "one URL gets the site" implies — say so.
      return data.error ? `Discovery failed — continuing with the given address: ${data.error}` : 'No discovery possible — continuing with the given address';
    }
    const scope = isObj(data.scope) ? data.scope : {};
    const found = scope.discovered || (Array.isArray(data.urls) ? data.urls.length : 0);
    let line = `${found} pages found`;
    if (data.strategy) line += ` (${data.strategy})`;
    if (scope.appliedPrefix) line += ` · ${scope.inScope || 0} under ${scope.appliedPrefix}`;
    const overCap = (isObj(data.dropped) ? data.dropped.overCap : 0) || scope.overCap || 0;
    if (overCap) line += ` · ${overCap} deeper pages over the limit dropped`;
    return line;
  }
  if (step === 'select_pages') return data.selected === false ? null : `kept ${data.kept} of ${data.considered} pages`;
  if (step === 'collect_sources') {
    const n = data.collected != null ? data.collected : Array.isArray(data.sources) ? data.sources.length : 0;
    const failed = Array.isArray(data.failures) ? data.failures.length : 0;
    return failed ? `${n} collected · ${failed} failed` : `${n} collected`;
  }
  if (step === 'chunk_corpus' || step === 'generate_chunks') return data.count != null ? `${data.count} chunks` : null;
  if (step === 'persist_to_hub' || step === 'persist_chunks') return data.persisted != null ? `${data.persisted} saved` : null;
  if (step === 'generate_questions') return data.count != null ? `${data.count} questions` : null;
  return null;
}

/** The largest sections of a discovered site, so an operator can see its shape and narrow the scope. */
export function topSections(histogram: unknown, limit = 6): { path: string; count: number }[] {
  if (!isObj(histogram)) return [];
  return Object.entries(histogram)
    .map(([path, count]) => ({ path, count: Number(count) }))
    .sort((a, b) => b.count - a.count)
    .slice(0, limit);
}

// ── pauses ──────────────────────────────────────────────────────────────────

/** Why a job is paused: a usage limit waits for a clock, a chunk-count drop waits for a person. */
export function pauseKind(reason: string | null | undefined): string {
  const r = String(reason || '');
  if (r.includes('weekly')) return 'weekly_limit';
  if (r.includes('upstream')) return 'upstream_error';
  if (r.includes('session')) return 'session_limit';
  if (r.includes('shrink')) return 'chunk_shrink';
  return '';
}

/** Answered with Approve (approve-shrink), not Resume — resume alone re-enters the same guard and parks again. */
export function isApprovalPause(reason: string | null | undefined): boolean {
  return pauseKind(reason) === 'chunk_shrink';
}

const PAUSE_LABEL: Record<string, string> = {
  session_limit: 'Session limit',
  weekly_limit: 'Weekly limit',
  upstream_error: 'Claude API error',
  chunk_shrink: 'Fewer chunks than now — approve to continue',
};

export function pauseLabel(reason: string | null | undefined): string {
  return PAUSE_LABEL[pauseKind(reason)] || '';
}

/** Only a session limit with a reset Claude actually stated is shown as a fact (weekly / estimated times are guesses). */
export function hasStatedResume(reason: string | null | undefined): boolean {
  return pauseKind(reason) === 'session_limit' && !String(reason || '').includes('estimated');
}

// ── labels ──────────────────────────────────────────────────────────────────

const STATUS_LABEL: Record<string, string> = {
  running: 'Running',
  pending: 'Waiting',
  succeeded: 'Succeeded',
  failed: 'Failed',
  cancelled: 'Cancelled',
  paused: 'Paused',
};
export const STATUS_TONE: Record<string, string> = {
  running: 'bg-blue-50 text-blue-700',
  pending: 'bg-violet-50 text-violet-700',
  succeeded: 'bg-green-50 text-green-700',
  failed: 'bg-red-50 text-red-700',
  cancelled: 'bg-orange-50 text-orange-700',
  paused: 'bg-yellow-50 text-yellow-800',
};
const TYPE_LABEL: Record<string, string> = {
  build_corpus: 'Crawl + chunk',
  index_corpus: 'Re-index',
  reindex: 'Re-index',
  chunk: 'Chunk',
  crawl: 'Collect pages',
  index: 'Index',
  genq: 'Questions',
  eval: 'Evaluation',
  copy: 'Copy',
};

export const statusLabel = (s: string | null | undefined): string => STATUS_LABEL[String(s)] || s || '—';
export const typeLabel = (t: string | null | undefined): string => TYPE_LABEL[String(t)] || t || '—';

const TERMINAL_STATUS = ['succeeded', 'failed', 'cancelled'];
/** Postgres (the job row) is the authority: three backend paths finish a job without emitting an event. */
export const isTerminalStatus = (s: string | null | undefined): boolean => TERMINAL_STATUS.includes(String(s || ''));

const STEP_ICON: Record<string, string> = { step_started: '⏳', step_completed: '✓', step_skipped: '⏭', step_failed: '✗' };
/** A step still "started" when the job is over stopped there: ⏹, not ⏳. */
export function stepIcon(type: string, jobTerminal: boolean): string {
  if (jobTerminal && type === 'step_started') return '⏹';
  return STEP_ICON[type] || '•';
}

// ── usage ───────────────────────────────────────────────────────────────────

export const fmtTokens = (n: unknown): string => {
  const v = Number(n) || 0;
  return v >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(v);
};
export const fmtCost = (usd: unknown): string => `$${(Number(usd) || 0).toFixed(4)}`;
export const fmtTime = (v: unknown): string => {
  if (!v) return '';
  const d = new Date(String(v));
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
};
/** A model id back to the tier an operator picks (claude-opus-5[1m] → opus[1m]); anything else stays whole. */
export function modelShort(id: unknown): string {
  const s = String(id || '');
  const m = /claude-(opus|sonnet|haiku)/i.exec(s);
  if (!m) return s;
  const tier = m[1].toLowerCase();
  return /\[1m\]/.test(s) ? `${tier}[1m]` : tier;
}
export const modelSummary = (models: unknown): string =>
  (Array.isArray(models) ? models : []).map((m) => modelShort(m && (m as { model?: string }).model)).join(' + ');

// ── stream ──────────────────────────────────────────────────────────────────

export interface JobEvent {
  type: string;
  step?: string;
  attempt?: number;
  error?: string;
  data?: any;
}

export interface JobStreamState {
  events: JobEvent[];
  status: string;
  result: any;
  error: string | null;
  resumeAt: string | null;
  pausedReason: string | null;
  retries: { attempt: number; error: string }[];
  failedAttempt: number | null;
}

export const INITIAL_JOB_STATE: JobStreamState = {
  events: [],
  status: 'pending',
  result: null,
  error: null,
  resumeAt: null,
  pausedReason: null,
  retries: [],
  failedAttempt: null,
};

const TERMINAL_EVENTS = ['job_succeeded', 'job_failed', 'job_cancelled'];
export const isTerminal = (type: string): boolean => TERMINAL_EVENTS.includes(type);
/**
 * Only success and cancellation close the stream from the client. A failed attempt may be retried, and only the server
 * knows: it ends the stream after the last event (v1 job c45aa503 looped on a client-side close).
 */
export const closesStream = (type: string): boolean => type === 'job_succeeded' || type === 'job_cancelled';

function addRetry(retries: JobStreamState['retries'], attempt: number, error: string) {
  return retries.some((r) => r.attempt === attempt) ? retries : [...retries, { attempt, error }];
}

export function jobStreamReducer(state: JobStreamState, event: JobEvent): JobStreamState {
  const s: JobStreamState = { ...state, events: [...state.events, event] };
  switch (event.type) {
    case 'job_started':
      // A later attempt after a job_failed means that failure was retried, not final.
      if (s.status === 'failed' && s.error && s.failedAttempt != null && (event.attempt || 0) > s.failedAttempt) {
        s.retries = addRetry(s.retries, s.failedAttempt, s.error);
      }
      s.status = 'running';
      s.error = null;
      break;
    case 'job_retrying':
      s.status = 'running';
      s.error = null;
      s.retries = addRetry(s.retries, event.attempt ?? 0, event.error || '');
      break;
    case 'job_succeeded':
      s.status = 'succeeded';
      s.error = null;
      s.result = event.data ?? null;
      break;
    case 'job_failed':
      s.status = 'failed';
      s.error = event.error || 'failed';
      s.failedAttempt = event.attempt != null ? event.attempt : s.failedAttempt;
      break;
    case 'job_cancelled':
      s.status = 'cancelled';
      break;
    case 'job_paused':
      s.status = 'paused';
      s.resumeAt = event.data?.resumeAt ?? null;
      s.pausedReason = event.data?.pausedReason ?? null;
      break;
    default:
      break;
  }
  return s;
}

/** `job_succeeded.data` as one line. */
export function resultSummary(result: unknown): string | null {
  if (!isObj(result)) return null;
  if (typeof result.persisted === 'number') {
    return `${result.persisted} chunks saved to the bot’s documents${result.deletedPrior ? ` (${result.deletedPrior} old documents replaced)` : ''}`;
  }
  if (result.snapshot_no || result.snapshotId || result.changed !== undefined) {
    return `Snapshot${result.snapshot_no ? ` #${result.snapshot_no}` : ''} built${typeof result.changed === 'number' ? ` (${result.changed} sources changed)` : ''}`;
  }
  return JSON.stringify(result);
}

export interface SseFrame {
  id: string | null;
  event: string;
  data: string;
}

/**
 * Incremental text/event-stream parser: push decoded text as it arrives, get the complete frames back. Comments
 * (`: ping`) and data-less frames are dropped; multi-line `data:` is joined with \n (the SSE rule).
 */
export function createSseParser(): { push(text: string): SseFrame[] } {
  let buf = '';
  let lastId: string | null = null;
  let pendingCr = false; // a chunk ending in \r may be the first half of \r\n (sse-starlette writes CRLF)
  return {
    push(text: string): SseFrame[] {
      let t = (pendingCr ? '\r' : '') + text;
      pendingCr = t.endsWith('\r');
      if (pendingCr) t = t.slice(0, -1);
      buf += t.replace(/\r\n?/g, '\n');
      const out: SseFrame[] = [];
      let i: number;
      while ((i = buf.indexOf('\n\n')) >= 0) {
        const block = buf.slice(0, i);
        buf = buf.slice(i + 2);
        let event = 'message';
        const data: string[] = [];
        for (const line of block.split('\n')) {
          if (!line || line.startsWith(':')) continue;
          const c = line.indexOf(':');
          const field = c < 0 ? line : line.slice(0, c);
          const value = c < 0 ? '' : line.slice(c + 1).replace(/^ /, '');
          if (field === 'id') lastId = value;
          else if (field === 'event') event = value;
          else if (field === 'data') data.push(value);
        }
        if (data.length) out.push({ id: lastId, event, data: data.join('\n') });
      }
      return out;
    },
  };
}
